import { Button, Chip, Tab, TabList, TabPanel, Tabs } from '@atlas/ui-primitives'
import { useParams } from 'react-router-dom'
import { extractApiErrorMessage } from '@/adapters/workflow-admin-http-error'
import { PageHeader } from '@/components/editorial/PageHeader'
import { DeletePromptDialog } from '@/components/prompts/DeletePromptDialog'
import { PromptLifecyclePill } from '@/components/prompts/PromptLifecyclePill'
import { PublishPromptDialog } from '@/components/prompts/PublishPromptDialog'
import type { Prompt } from '@/domain/prompt.types'
import { PROMPT_TYPE_LABEL } from '@/domain/prompt.types'
import { promptDraftState } from '@/domain/prompt-draft-state'
import {
	useGenerateOutputSchema,
	usePrompt,
	usePromptLists,
	usePromptVersions,
	useRenderPrompt,
} from '@/hooks/usePrompts'
import { PromptClassificationTab } from './PromptClassificationTab'
import { EditorTab } from './PromptEditorTab'
import { PromptEvalTab } from './PromptEvalTab'
import { PromptVersionsTab } from './PromptVersionsTab'
import { usePromptDeletion, usePromptEditor } from './usePromptEditor'

const EDITOR_TAB = 'editor'

export function PromptDetailPage() {
	const { id } = useParams<{ id: string }>()
	const query = usePrompt(id ?? null)

	if (query.isLoading) return <p className="text-ds-text-muted">Loading…</p>
	if (query.isError || !query.data) {
		return (
			<p role="alert" className="rounded-ds-card border border-ds-danger p-8 text-center text-ds-danger">
				This prompt could not be loaded.
			</p>
		)
	}

	// Split so `usePromptEditor` never sits behind an early return.
	return <PromptDetailView prompt={query.data} />
}

function PromptDetailView({ prompt }: { prompt: Prompt }) {
	const editor = usePromptEditor(prompt)
	const deletion = usePromptDeletion(prompt)
	const lists = usePromptLists()
	const render = useRenderPrompt()
	const suggestSchema = useGenerateOutputSchema()

	return (
		<div>
			<PageHeader
				eyebrow="Prompt"
				title={prompt.name}
				subtitle={prompt.description ?? undefined}
				actions={<HeaderActions editor={editor} deletion={deletion} />}
			/>

			<div className="mb-4 flex flex-wrap items-center gap-2">
				<span className="rounded-ds-md bg-ds-surface px-1.5 py-0.5 font-mono text-ds-2xs text-ds-text-subtle">
					{prompt.code}
				</span>
				<Chip variant="muted" size="sm">
					{PROMPT_TYPE_LABEL[prompt.type]}
				</Chip>
				<Chip variant="muted" size="sm">
					{prompt.currentVersion === 0 ? 'Unpublished' : `v${prompt.currentVersion}`}
				</Chip>
				<PromptLifecyclePill lifecycle={prompt.lifecycle} />
			</div>

			<DeletePromptDialog
				prompt={prompt}
				open={deletion.confirming}
				isDeleting={deletion.isDeleting}
				error={deletion.error}
				onConfirm={deletion.confirm}
				onCancel={deletion.cancel}
			/>

			{editor.saveFailed ? (
				<p role="alert" className="mb-4 text-ds-sm text-ds-danger">
					The prompt could not be saved. Your changes are still here — try again.
				</p>
			) : null}

			<DraftStateNotice prompt={prompt} />

			<PublishPromptDialog
				open={editor.publishOpen}
				promptName={prompt.name}
				gate={editor.evalGate}
				isLoadingGate={editor.isLoadingEvalGate}
				isPublishing={editor.isPublishing}
				errorMessage={
					// The tenant's gate refuses with a message worth reading verbatim, which
					// lives in the response envelope rather than on `error.message` (axios puts
					// the status line there).
					editor.publishFailed
						? extractApiErrorMessage(editor.publishError, 'Publishing failed. Nothing was frozen.')
						: null
				}
				onConfirm={editor.confirmPublish}
				onCancel={editor.cancelPublish}
			/>

			<Tabs defaultSelectedKey={EDITOR_TAB}>
				<TabList variant="underline" className="mb-4" aria-label="Prompt sections">
					<Tab id={EDITOR_TAB}>Editor</Tab>
					<Tab id="versions">Versions</Tab>
					<Tab id="evals">Test cases</Tab>
					<Tab id="classification">Classification</Tab>
				</TabList>

				<TabPanel id={EDITOR_TAB}>
					<EditorTab
						prompt={prompt}
						editor={editor}
						lists={lists.data ?? []}
						render={render}
						suggestSchema={suggestSchema}
					/>
				</TabPanel>

				<TabPanel id="versions">
					<PromptVersionsTab prompt={prompt} />
				</TabPanel>

				<TabPanel id="evals">
					<PromptEvalTab prompt={prompt} />
				</TabPanel>

				<TabPanel id="classification">
					<PromptClassificationTab prompt={prompt} />
				</TabPanel>
			</Tabs>
		</div>
	)
}

/**
 * Says whether the prompt anyone resolves matches the one on screen.
 *
 * Editing a prompt does NOT create a version — only publishing does — and runtime
 * resolves the `production` label to a frozen version, never the editable head.
 * So an author could edit, save successfully, and reasonably believe the change
 * was live when nothing had changed for callers. Nothing said so anywhere.
 *
 * Silent on a published prompt with no pending edits: a banner that is always
 * there stops being read.
 */
function DraftStateNotice({ prompt }: { prompt: Prompt }) {
	const versions = usePromptVersions(prompt.id)
	// While the versions load, saying nothing beats guessing — a wrong "unpublished"
	// is the same false alarm the banner exists to prevent.
	if (versions.isLoading || versions.isError) return null

	const state = promptDraftState(prompt, versions.data ?? [])
	if (state === 'published') return null

	return (
		<p className="mb-4 rounded-ds-md border border-ds-warning bg-ds-surface p-3 text-ds-sm text-ds-text">
			{state === 'never-published' ? (
				<>
					<strong className="text-ds-warning">Never published.</strong> Nothing resolves this prompt yet — publishing
					freezes the current body and variables as version 1.
				</>
			) : (
				<>
					<strong className="text-ds-warning">Unpublished changes.</strong> Callers still get v{prompt.currentVersion}.
					Publish to make what you see here the version they resolve.
				</>
			)}
		</p>
	)
}

/**
 * The header's controls, which differ entirely between reading and editing.
 *
 * Edit mode offers Cancel and Save only: publishing or deleting mid-edit would act on
 * a prompt whose unsaved changes are still on screen.
 */
function HeaderActions({
	editor,
	deletion,
}: {
	editor: ReturnType<typeof usePromptEditor>
	deletion: ReturnType<typeof usePromptDeletion>
}) {
	if (editor.editing) {
		return (
			<div className="flex gap-2">
				<Button variant="secondary" onPress={editor.cancelEdit} isDisabled={editor.isSaving}>
					Cancel
				</Button>
				<Button onPress={editor.save} isDisabled={editor.isSaving || !editor.canSave}>
					{editor.isSaving ? 'Saving…' : 'Save'}
				</Button>
			</div>
		)
	}

	return (
		<div className="flex gap-2">
			<Button variant="secondary" onPress={editor.startEdit}>
				Edit
			</Button>
			<Button onPress={editor.openPublish} isDisabled={editor.isPublishing}>
				{editor.isPublishing ? 'Publishing…' : 'Publish'}
			</Button>
			<Button variant="muted" onPress={deletion.open}>
				Delete
			</Button>
		</div>
	)
}
